import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/lib/auth';
import Landing from '@/pages/Landing';
import { Analytics } from '@/components/Analytics';
import { DisputesGate } from '@/components/DisputesGate';
import '@/App.css';

// Only the marketing homepage ships in the first bundle. Every portal is a separate chunk,
// fetched when someone actually navigates to it — a visitor reading the homepage should not
// have to download the admin console, the buyer app and the seller app first.
const lazyFrom = (loader, name) => lazy(() => loader().then((m) => ({ default: m[name] })));

const adminPages = () => import('@/pages/admin/AdminPages');
const teamPages = () => import('@/pages/admin/TeamPages');
const adminV1Pages = () => import('@/pages/admin/AdminV1Pages');
const marketplacePages = () => import('@/pages/admin/MarketplacePages');
const queuePages = () => import('@/pages/admin/QueuePages');
const paymentPages = () => import('@/pages/admin/PaymentPages');
const fulfilmentPages = () => import('@/pages/admin/FulfilmentPages');
const commercialPages = () => import('@/pages/admin/CommercialPages');
const insightPages = () => import('@/pages/admin/InsightPages');
const financePages = () => import('@/pages/admin/FinancePages');
const screeningPages = () => import('@/pages/admin/ScreeningPages');

const AdminLayout = lazyFrom(adminPages, 'AdminLayout');
const AdminDashboard = lazyFrom(adminPages, 'AdminDashboard');
const AdminConfig = lazyFrom(adminPages, 'AdminConfig');
const AdminSellers = lazyFrom(adminPages, 'AdminSellers');
const AdminCustomers = lazyFrom(adminPages, 'AdminCustomers');
const AdminNewEnquiry = lazyFrom(teamPages, 'AdminNewEnquiry');
const AdminNewQuotation = lazyFrom(teamPages, 'AdminNewQuotation');
const AdminUsers = lazyFrom(teamPages, 'AdminUsers');
const AdminSalesAllotment = lazyFrom(() => import('@/pages/admin/SalesAllotmentPages'), 'AdminSalesAllotment');
const AdminMasters = lazyFrom(adminV1Pages, 'AdminMasters');
const AdminRoles = lazyFrom(adminV1Pages, 'AdminRoles');
const AdminAudit = lazyFrom(adminV1Pages, 'AdminAudit');
const AdminDataCleanup = lazyFrom(() => import('@/pages/admin/DataCleanupPage'), 'AdminDataCleanup');
const AdminChatSafety = lazyFrom(() => import('@/pages/admin/ChatSafetyPage'), 'AdminChatSafety');
const AdminAccountDeletions = lazyFrom(() => import('@/pages/admin/AdminAccountDeletions'), 'AdminAccountDeletions');
const AdminMessaging = lazyFrom(() => import('@/pages/admin/AdminMessaging'), 'AdminMessaging');
const MktEnquiries = lazyFrom(marketplacePages, 'MktEnquiries');
const MktEnquiryDetail = lazyFrom(marketplacePages, 'MktEnquiryDetail');
const MktQuotations = lazyFrom(marketplacePages, 'MktQuotations');
const MktQuotationDetail = lazyFrom(marketplacePages, 'MktQuotationDetail');
const MktAcceptances = lazyFrom(queuePages, 'MktAcceptances');
const MktChatMonitor = lazyFrom(queuePages, 'MktChatMonitor');
const MktRoutingRules = lazyFrom(queuePages, 'MktRoutingRules');
const MktPaymentRequests = lazyFrom(paymentPages, 'MktPaymentRequests');
const MktPayments = lazyFrom(paymentPages, 'MktPayments');
const MktOrders = lazyFrom(paymentPages, 'MktOrders');
const MktOrderDetail = lazyFrom(paymentPages, 'MktOrderDetail');
const MktFulfilment = lazyFrom(fulfilmentPages, 'MktFulfilment');
const MktFulfilmentDetail = lazyFrom(fulfilmentPages, 'MktFulfilmentDetail');
const MktDisputes = lazyFrom(commercialPages, 'MktDisputes');
const MktDisputeDetail = lazyFrom(commercialPages, 'MktDisputeDetail');
const MktReturns = lazyFrom(insightPages, 'MktReturns');
const MktCancellations = lazyFrom(insightPages, 'MktCancellations');
const MktRefunds = lazyFrom(financePages, 'MktRefunds');
const MktRecoveries = lazyFrom(financePages, 'MktRecoveries');
const MktPayouts = lazyFrom(financePages, 'MktPayouts');
const MktPayoutDetail = lazyFrom(financePages, 'MktPayoutDetail');
const MktFinanceRecords = lazyFrom(financePages, 'MktFinanceRecords');
const MktRatings = lazyFrom(financePages, 'MktRatings');
const MktReviewQueue = lazyFrom(screeningPages, 'MktReviewQueue');
const AdminAccessoryKeywords = lazyFrom(screeningPages, 'AdminAccessoryKeywords');

const Login = lazyFrom(() => import('@/pages/Auth'), 'Login');
const Pay = lazy(() => import('@/pages/Pay'));
const Feedback = lazy(() => import('@/pages/Feedback'));
const Policy = lazy(() => import('@/pages/Policy'));
const AccountDeletion = lazy(() => import('@/pages/AccountDeletion'));
const Buy = lazy(() => import('@/pages/Buy'));
const Sell = lazy(() => import('@/pages/Sell'));
const SetPassword = lazy(() => import('@/pages/SetPassword'));
const CustomerApp = lazy(() => import('@/customer/CustomerApp'));
const SellerApp = lazy(() => import('@/seller/SellerApp'));

function LegacyBuyerRedirect() {
  const { pathname, search } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/customer/, '/buyer')}${search}`} replace />;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen grid place-items-center bg-[#F8FAFC]">
      <div className="text-center">
        <div className="inline-block w-8 h-8 rounded-lg bg-emerald-600 animate-pulse" />
        <div className="mt-3 text-xs uppercase tracking-wider text-[#6B7280]">Loading SparesHub…</div>
      </div>
    </div>
  );
}

const ADMIN_PORTAL_ROLES = ['admin', 'manager', 'ops', 'finance', 'sales', 'purchase'];

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!ADMIN_PORTAL_ROLES.includes(user.role)) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function AdminHome() {
  const { user } = useAuth();
  if (user?.role === 'sales') return <Navigate to="/admin/mkt/new-enquiry" replace />;
  if (user?.role === 'purchase') return <Navigate to="/admin/mkt/new-quotation" replace />;
  return <AdminDashboard />;
}

function LanguageQuerySync() {
  const { i18n } = useTranslation();
  const { search } = useLocation();
  useEffect(() => {
    const q = new URLSearchParams(search).get('lang');
    if (q && ['en', 'hi', 'mr', 'kn', 'ta', 'gu', 'ml'].includes(q) && i18n.language !== q) {
      i18n.changeLanguage(q);
    }
  }, [search, i18n]);
  return null;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (user && ADMIN_PORTAL_ROLES.includes(user.role)) return <Navigate to="/admin" replace />;
  return <Landing />;
}

function LangEntry() {
  const { pathname } = useLocation();
  const seg = pathname.replace(/^\/+/, '').split('/')[0];
  const target = ['en', 'hi', 'kn', 'ta'].includes(seg) ? seg : 'en';
  return <Navigate to={`/?lang=${target}`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
        <Analytics />
        <LanguageQuerySync />
        <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/en" element={<LangEntry />} />
          <Route path="/hi" element={<LangEntry />} />
          <Route path="/kn" element={<LangEntry />} />
          <Route path="/ta" element={<LangEntry />} />
          <Route path="/login" element={<Login />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/pay/:token" element={<Pay />} />
          <Route path="/feedback/:token" element={<Feedback />} />
          <Route path="/legal/:slug" element={<Policy />} />
          <Route path="/account-deletion" element={<AccountDeletion />} />
          {/* The spellings app-store reviewers look for, all one page. */}
          <Route path="/data-deletion" element={<Navigate to="/account-deletion" replace />} />
          <Route path="/delete-account" element={<Navigate to="/account-deletion" replace />} />
          <Route path="/sell" element={<Sell />} />
          <Route path="/become-a-seller" element={<Navigate to="/sell" replace />} />
          <Route path="/buy" element={<Buy />} />
          <Route path="/buyers" element={<Navigate to="/buy" replace />} />
          <Route path="/buyer/*" element={<CustomerApp />} />
          <Route path="/customer/*" element={<LegacyBuyerRedirect />} />
          <Route path="/seller/*" element={<SellerApp />} />
          <Route path="/admin" element={<Protected><AdminLayout /></Protected>}>
            <Route index element={<AdminHome />} />
            {/* Banners, announcements and FAQs were retired from the console (2026-08-27). */}
            <Route path="banners" element={<Navigate to="/admin" replace />} />
            <Route path="announcements" element={<Navigate to="/admin" replace />} />
            <Route path="faqs" element={<Navigate to="/admin" replace />} />
            <Route path="config" element={<AdminConfig />} />
            <Route path="sellers" element={<AdminSellers />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="account-deletions" element={<AdminAccountDeletions />} />
            <Route path="messaging" element={<AdminMessaging />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="sales-allotment" element={<AdminSalesAllotment />} />
            <Route path="data-cleanup" element={<AdminDataCleanup />} />
            <Route path="chat-safety" element={<AdminChatSafety />} />
            <Route path="masters" element={<AdminMasters />} />
            <Route path="roles" element={<AdminRoles />} />
            <Route path="audit" element={<AdminAudit />} />
            {/* Ops Board + Metrics are deactivated — components stay for a future switch-on. */}
            <Route path="mkt" element={<Navigate to="/admin" replace />} />
            <Route path="mkt/enquiries" element={<MktEnquiries />} />
            <Route path="mkt/review-queue" element={<MktReviewQueue />} />
            <Route path="keywords" element={<AdminAccessoryKeywords />} />
            <Route path="mkt/new-enquiry" element={<AdminNewEnquiry />} />
            <Route path="mkt/enquiries/:eid" element={<MktEnquiryDetail />} />
            <Route path="mkt/quotations" element={<MktQuotations />} />
            <Route path="mkt/new-quotation" element={<AdminNewQuotation />} />
            <Route path="mkt/quotations/:qid" element={<MktQuotationDetail />} />
            <Route path="mkt/acceptances" element={<MktAcceptances />} />
            {/* The stock-confirmation step was retired: acceptance goes straight to payment. */}
            <Route path="mkt/stock" element={<Navigate to="/admin" replace />} />
            {/* The post-payment fitment check was retired: a paid order dispatches straight away. */}
            <Route path="mkt/fitments" element={<Navigate to="/admin" replace />} />
            <Route path="mkt/fitments/:fid" element={<Navigate to="/admin" replace />} />
            <Route path="mkt/payment-requests" element={<MktPaymentRequests />} />
            <Route path="mkt/payments" element={<MktPayments />} />
            <Route path="mkt/orders" element={<MktOrders />} />
            <Route path="mkt/orders/:oid" element={<MktOrderDetail />} />
            <Route path="mkt/fulfilment" element={<MktFulfilment />} />
            <Route path="mkt/fulfilment/:oid" element={<MktFulfilmentDetail />} />
            <Route path="mkt/disputes" element={<DisputesGate to="/admin"><MktDisputes /></DisputesGate>} />
            <Route path="mkt/disputes/:did" element={<DisputesGate to="/admin"><MktDisputeDetail /></DisputesGate>} />
            <Route path="mkt/refunds" element={<MktRefunds />} />
            <Route path="mkt/recoveries" element={<MktRecoveries />} />
            <Route path="mkt/payouts" element={<MktPayouts />} />
            <Route path="mkt/payouts/:pid" element={<MktPayoutDetail />} />
            <Route path="mkt/finance-records" element={<MktFinanceRecords />} />
            <Route path="mkt/ratings" element={<MktRatings />} />
            <Route path="mkt/metrics" element={<Navigate to="/admin" replace />} />
            <Route path="mkt/returns" element={<MktReturns />} />
            <Route path="mkt/cancellations" element={<MktCancellations />} />
            <Route path="mkt/chat" element={<MktChatMonitor />} />
            <Route path="mkt/routing" element={<MktRoutingRules />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
